// ============================================================
// useReservationSave — カレンダー編集パネルの状態と保存系ハンドラ
//
// GridV2 から抽出（2026-07-02 Phase 1）。編集パネル state（editing/isSaving/
// dirty 追跡）と、DnD 起点の保存3経路（リサイズ / ドラッグ新規作成 / 編集保存）
// を1モジュールに集約し、インタラクション層再実装（Phase 2）の差し替え面を
// 最小化する。
//
// 保存ルール（md/master_md/ref-save-reservation.md）:
//   - リサイズ: detail を送らない toScheduleOnlyPayload 経由
//   - 編集保存(更新): fresh fetch + merge + __completeDetail で完全 detail を送る
//   - 新規: buildDetailPayload(isNew=true) が既定値で完全 detail に補完
// ============================================================

import { useState, useCallback, useRef, useEffect } from 'react';
import { Reservation, Vehicle, StatusItem, EditingReservation } from './types';
import { buildMemoEntry, appendMemoEntry } from './memoLog';
import { toScheduleOnlyPayload } from './schedulePayload';
import { saveScheduleChange } from './saveScheduleChange';
import { saveDispatchReservation, fetchDispatchReservations } from '../../api/dispatchService';
import { reservationSyncService } from '../../services/reservationSyncService';
import { beginOptimistic, settleOptimistic, rollbackOptimistic } from './optimisticCache';
import { StagingController } from './useStaging';

interface UseReservationSaveProps {
  /** フィルタ済み車両（新規予約の car_type_id 解決用） */
  vehicles: Vehicle[];
  /** 新規予約の booked ステータス解決用 */
  reservationStatusList: StatusItem[];
  showToast: (message: string, severity?: 'success' | 'error', onUndo?: () => void) => void;
  /** ステージングモード: active 中はリサイズを保存せず stage() に積む */
  staging?: StagingController;
}

interface UseReservationSaveReturn {
  editing: EditingReservation | null;
  setEditing: React.Dispatch<React.SetStateAction<EditingReservation | null>>;
  isSaving: boolean;
  /** 編集パネルが開いた時のスナップショットと差分があるか */
  isEditingDirty: () => boolean;
  /** overlay 上下端ドラッグによるリサイズ保存（detail 非送信） */
  handleReservationResize: (r: Reservation, edge: 'top' | 'bottom', newDate: Date) => Promise<void>;
  /** ドラッグで新規予約ドラフトを作成して編集パネルを開く */
  handleDragCreate: (vehicleId: string, startDate: Date, endDate: Date) => void;
  /**
   * 編集パネルの保存。
   * @param data 編集パネル（useReservationEditor.saveMerged）が三方向マージ済みのペイロードを渡す。
   *             省略時（旧経路）は editing を fresh fetch とマージして送る。
   */
  handleSave: (data?: Record<string, any> | null, opts?: { keepOpen?: boolean }) => Promise<void>;
}

export function useReservationSave({
  vehicles,
  reservationStatusList,
  showToast,
  staging,
}: UseReservationSaveProps): UseReservationSaveReturn {
  // 編集パネル state
  const [editing, setEditing] = useState<EditingReservation | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  // 編集中かどうかを追跡（開いた時のスナップショットと比較）
  const editingOriginalRef = useRef<string | null>(null);
  const editingIdRef = useRef<string | null>(null);
  useEffect(() => {
    const currentId = editing ? String(editing.id ?? editing.page_id ?? '') : null;
    if (editing && currentId !== editingIdRef.current) {
      // 新しい予約を開いた → スナップショット保存
      editingOriginalRef.current = JSON.stringify(editing);
      editingIdRef.current = currentId;
    } else if (!editing) {
      editingOriginalRef.current = null;
      editingIdRef.current = null;
    }
  }, [editing]);
  const isEditingDirty = useCallback(() => {
    if (!editing || !editingOriginalRef.current) return false;
    return JSON.stringify(editing) !== editingOriginalRef.current;
  }, [editing]);

  // リサイズ（overlay 上下端ドラッグ）
  const handleReservationResize = useCallback(async (r: Reservation, edge: 'top' | 'bottom', newDate: Date) => {
    // 新規予約ゴーストのリサイズ → editing のみ更新、API 呼び出しなし
    // ※ editing クロージャは古い可能性があるため、ID パターンで判定
    if (String(r.id).startsWith('tmp_')) {
      if (edge === 'top') {
        setEditing(prev => prev ? { ...prev, start_date: newDate.toISOString() } : null);
      } else {
        setEditing(prev => prev ? { ...prev, end_date: newDate.toISOString() } : null);
      }
      return;
    }
    // ステージングモード: 保存せずローカルに積むだけ
    if (staging?.active) {
      staging.stage(
        r,
        edge === 'top' ? { start_date: newDate.toISOString() } : { end_date: newDate.toISOString() },
        edge === 'top' ? '貸出日時変更' : '返却日時変更',
      );
      return;
    }
    const original = { ...r };
    const oldDate = edge === 'top' ? r.start_date : r.end_date;
    let updated = { ...r };
    if (edge === 'top') {
      updated.start_date = newDate.toISOString();
    } else {
      updated.end_date = newDate.toISOString();
    }

    // メモ追記（楽観更新用。永続化は saveScheduleChange が fresh のメモに entry を追記する）
    const label = edge === 'top' ? '貸出日時変更' : '返却日時変更';
    const oldShort = new Date(oldDate).toLocaleString('ja', { month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit' });
    const newShort = newDate.toLocaleString('ja', { month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit' });
    const memoEntry = buildMemoEntry(label, `${oldShort} → ${newShort}`);
    updated = { ...updated, memo: appendMemoEntry(updated.memo, memoEntry) };

    // 楽観的更新（保存確定まで同期による上書きから保護する。optimisticCache 参照）
    beginOptimistic([updated]);

    // Undo はスケジュールのみ復元（変更ログはサーバーに残す）
    const undoPayload = toScheduleOnlyPayload(original);

    // Undo 付きトースト
    const undoFn = async () => {
      beginOptimistic([original]);
      try {
        const undone = await saveDispatchReservation(undoPayload);
        settleOptimistic([original.id], undone);
        showToast('元に戻しました');
      } catch {
        rollbackOptimistic([updated]);
        showToast('元に戻す操作に失敗しました', 'error');
      }
    };

    try {
      const saved = await saveScheduleChange(updated, memoEntry);
      // サーバー確定値をキャッシュへ（Pusher エコー / ポーリングを待たない）
      settleOptimistic([updated.id], saved);
      showToast(
        edge === 'top' ? '貸出日時を変更しました' : '返却日時を変更しました',
        'success',
        undoFn,
      );
    } catch (e) {
      rollbackOptimistic([original]);
      showToast('更新に失敗しました', 'error');
    }
  }, [showToast, staging]);

  // ドラッグで新規予約
  const handleDragCreate = useCallback((vehicleId: string, startDate: Date, endDate: Date) => {
    // 新規予約編集中はドラッグ作成しない
    if (editing && editing.__isNew) return;
    // 既存予約の編集中 → 編集済みなら破棄確認、未編集なら閉じ→開き
    if (editing && !editing.__isNew) {
      if (isEditingDirty()) {
        if (!window.confirm('編集中の内容が破棄されます。新規予約を作成しますか？')) return;
      }
    }
    const bookedStatus = reservationStatusList.find(s => s.code === 'booked');
    const vehicle = vehicles.find(v => String(v.id) === vehicleId);
    const newReservation = {
      id: `tmp_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
      vehicle_id: vehicleId,
      start_date: startDate.toISOString(),
      end_date: endDate.toISOString(),
      customer_name: '',
      status: bookedStatus ? String(bookedStatus.id) : 'booked',
      bg_color: null,
      text_color: null,
      car_type_id: vehicle?.car_type_id || '',
      __isNew: true,
    } as EditingReservation;
    setEditing(newReservation);
  }, [reservationStatusList, editing, vehicles, isEditingDirty]);

  // 保存
  const handleSave = useCallback(async (data?: Record<string, any> | null, opts: { keepOpen?: boolean } = {}) => {
    if (!editing || isSaving) return;
    const isNew = !!editing.__isNew;
    setIsSaving(true);
    if (isNew) showToast('予約を作成中...');
    try {
      // 2026-09-07: 通常は編集パネル（useReservationEditor.saveMerged）が
      //   「サーバー最新 + 自分が変更したフィールドだけ」を三方向マージした
      //   ペイロード（__completeDetail 付き）を data で渡してくる。
      //   以前の `{ ...fresh, ...editing }` は editing の全フィールドが勝つため、
      //   パネルを開いている間に他の端末が保存した変更を先祖返りさせていた。
      let saveInput: any = data && !isNew ? data : editing;
      if (!isNew && !data) {
        // 旧経路（data 無し）のフォールバック。完全な detail を確保 (2026-06-20 修正 / 方針B):
        //   カレンダーのキャッシュ予約は detail を完全に保持していないことがあり、
        //   メモだけ等の sparse な detail を送ると backend が 500 を返す。
        try {
          const [fresh] = await fetchDispatchReservations({ id: editing.id });
          if (fresh) {
            saveInput = { ...fresh, ...editing };
            saveInput.detail = { ...(fresh.detail || {}), ...(editing.detail || {}) };
          }
        } catch (fe) {
          console.warn('[useReservationSave] fresh 取得に失敗、editing をそのまま保存します:', fe);
        }
        // 完全な detail で送る（sparse detail による 500 を回避）
        saveInput = { ...saveInput, __completeDetail: true };
      }
      const saved = await saveDispatchReservation(saveInput);
      // 楽観的更新: APIレスポンス(saved)をキャッシュに反映（updated_at が最新のため Pusher 競合を防止）
      if (isNew && saved?.id) {
        // 新規: tmp_ IDを削除し、実IDでキャッシュに追加
        reservationSyncService.cachedReservations.delete(String(editing.id));
        reservationSyncService.cachedReservations.set(String(saved.id), saved);
        const all = reservationSyncService.getReservations();
        reservationSyncService.listeners.forEach((cb: Function) => cb([], all));
      } else if (!isNew) {
        // サーバ確定値をキャッシュへ深マージ + local_update 通知（DnD 経路と同じ作法）
        reservationSyncService.applyLocalUpdate(saved || { ...saveInput, id: editing.id });
      }
      // Pusher 即時更新のため forceSync 不要
      showToast(isNew ? '予約を作成しました' : '予約を保存しました');
      if (!opts.keepOpen) {
        if (isNew) {
          setTimeout(() => setEditing(null), 1500);
        } else {
          setEditing(null);
        }
      }
    } catch (e) {
      console.error('[useReservationSave] save failed:', e);
      showToast('保存に失敗しました', 'error');
    } finally {
      setIsSaving(false);
    }
  }, [editing, isSaving, showToast]);

  return {
    editing,
    setEditing,
    isSaving,
    isEditingDirty,
    handleReservationResize,
    handleDragCreate,
    handleSave,
  };
}
